import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";

const PRIMARY = [231, 67, 22]; // #e74316
const DARK = [40, 40, 40];
const HEADER_DARK = [38, 38, 38];
const GRAY = [115, 108, 104]; // #736C68
const LIGHT_GRAY = [246, 246, 246];
const BORDER = [222, 217, 214];
const BADGE_BG = [253, 233, 224];
const GREEN = [39, 148, 90];
const RED = [201, 61, 51];
const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

// Crop window (px) isolating the logo mark + wordmark from the source raster,
// which ships with a lot of transparent padding around the actual artwork.
const LOGO_CROP = { x: 142, y: 400, w: 842, h: 262 };

const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

const formatAddress = (address) => {
  if (!address) return { line: "-", houseNo: "", city: "", zipcode: "" };
  return {
    line: address.address || "-",
    houseNo:
      address.houseNo ||
      address.pickupHousenoStno ||
      address.dropHousenoStno ||
      "",
    city: address.city || address.pickupCity || address.dropCity || "",
    zipcode:
      address.zipcode || address.pickupZipcode || address.dropZipcode || "",
  };
};

const loadImageAsDataUrl = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const loadLogoDataUrl = async () => {
  try {
    const svgRes = await fetch("/assets/img/quimeraLogo.svg");
    const svgText = await svgRes.text();
    const match = svgText.match(/data:image\/png;base64,[^"]+/);
    if (!match) return null;

    const img = new window.Image();
    const loaded = new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });
    img.src = match[0];
    await loaded;

    const canvas = document.createElement("canvas");
    canvas.width = LOGO_CROP.w;
    canvas.height = LOGO_CROP.h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      img,
      LOGO_CROP.x,
      LOGO_CROP.y,
      LOGO_CROP.w,
      LOGO_CROP.h,
      0,
      0,
      LOGO_CROP.w,
      LOGO_CROP.h,
    );
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
};

const drawImagePlaceholder = (doc, x, y, size) => {
  doc.setFillColor(...LIGHT_GRAY);
  doc.setDrawColor(...BORDER);
  doc.setLineDashPattern([0.8, 0.8], 0);
  doc.roundedRect(x, y, size, size, 1, 1, "FD");
  doc.setLineDashPattern([], 0);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...GRAY);
  doc.text("Item", x + size / 2, y + size / 2 - 1, { align: "center" });
  doc.text("photo", x + size / 2, y + size / 2 + 2.5, { align: "center" });
};

// Draws the logo + document title + order-id badge shared by every generated
// document. Returns the y position content should start at.
const drawBrandHeader = (
  doc,
  { marginX, pageWidth, logoDataUrl, title, orderId },
) => {
  const y = 12;
  if (logoDataUrl) {
    const logoWidth = 36;
    const logoHeight = logoWidth / (LOGO_CROP.w / LOGO_CROP.h);
    doc.addImage(logoDataUrl, "PNG", marginX, y, logoWidth, logoHeight);
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...PRIMARY);
    doc.text("QUIMERA", marginX, y + 7);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text("Logistics", marginX, y + 11.5);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...DARK);
  doc.text(title, pageWidth - marginX, y + 7, { align: "right" });

  const badgeY = y + 11;
  const orderIdText = `ORDER ID   ${orderId || "-"}`;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  const badgeWidth = doc.getTextWidth(orderIdText) + 8;
  doc.setFillColor(...BADGE_BG);
  doc.roundedRect(
    pageWidth - marginX - badgeWidth,
    badgeY,
    badgeWidth,
    6.5,
    1.2,
    1.2,
    "F",
  );
  doc.setTextColor(...PRIMARY);
  doc.text(orderIdText, pageWidth - marginX - badgeWidth / 2, badgeY + 4.5, {
    align: "center",
  });

  return 30;
};

// Draws the signature lines anchored near the bottom of the page (adding a
// page first if the content above would overlap it), then the contact
// footer on every page of the document.
const drawSignatureAndFooter = (
  doc,
  { y, marginX, pageWidth, contentWidth, leftLabel, rightLabel },
) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  const signatureLineY = pageHeight - 32;

  if (y > signatureLineY - 8) {
    doc.addPage();
  }

  const sigWidth = (contentWidth - 10) / 2;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.3);
  doc.line(marginX, signatureLineY, marginX + sigWidth, signatureLineY);
  doc.line(
    pageWidth - marginX - sigWidth,
    signatureLineY,
    pageWidth - marginX,
    signatureLineY,
  );
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY);
  doc.text(leftLabel, marginX, signatureLineY + 5);
  doc.text(rightLabel, pageWidth - marginX - sigWidth, signatureLineY + 5);

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    const footerY = doc.internal.pageSize.getHeight() - 12;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(marginX, footerY - 4, pageWidth - marginX, footerY - 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text("Quimera Logistics · Luanda, Angola", marginX, footerY);
    doc.text(
      "geral@quimera.ao · +244 934 049 576",
      pageWidth - marginX,
      footerY,
      {
        align: "right",
      },
    );
  }
};

export const generateWorkOrderPdf = async (order) => {
  if (!order) return;

  const products = order?.productDetails || [];
  const [logoDataUrl, productImages] = await Promise.all([
    loadLogoDataUrl(),
    Promise.all(
      products.map((p) =>
        p?.image
          ? loadImageAsDataUrl(`${backendUrl}${p.image}`)
          : Promise.resolve(null),
      ),
    ),
  ]);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 15;
  const contentWidth = pageWidth - marginX * 2;

  let y = drawBrandHeader(doc, {
    marginX,
    pageWidth,
    logoDataUrl,
    title: "WORK ORDER",
    orderId: order?.orderId,
  });

  // ---- Booking / pickup / customer type strip ----
  const infoColWidth = contentWidth / 3;
  const stripHeight = 16;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.rect(marginX, y, contentWidth, stripHeight);
  doc.setLineWidth(0.9);
  doc.setDrawColor(...PRIMARY);
  doc.line(marginX, y, pageWidth - marginX, y);
  doc.setLineWidth(0.2);
  doc.setDrawColor(...BORDER);
  doc.line(
    marginX + infoColWidth,
    y + 2,
    marginX + infoColWidth,
    y + stripHeight - 2,
  );
  doc.line(
    marginX + infoColWidth * 2,
    y + 2,
    marginX + infoColWidth * 2,
    y + stripHeight - 2,
  );

  const infoRow = [
    [
      "BOOKING DATE",
      order?.createdAt ? moment(order.createdAt).format("DD MMMM YYYY") : "-",
    ],
    [
      "PICKUP DATE",
      order?.pickupTime ? moment(order.pickupTime).format("DD MMMM YYYY") : "-",
    ],
    [
      "CUSTOMER TYPE",
      order?.accountType
        ? `${capitalize(order.accountType)} user`
        : "Individual user",
    ],
  ];
  infoRow.forEach(([label, value], idx) => {
    const x = marginX + idx * infoColWidth + 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY);
    doc.text(label, x, y + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(...DARK);
    doc.text(value, x, y + 12);
  });

  y += stripHeight + 9;

  const sectionHeading = (label) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...PRIMARY);
    doc.text(label, marginX, y);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(marginX, y + 2, pageWidth - marginX, y + 2);
    y += 8;
  };

  // ---- Customer information ----
  sectionHeading("CUSTOMER INFORMATION");
  const custRow = [
    ["NAME", order?.name || "-"],
    ["PHONE NUMBER", order?.phoneNumber || "-"],
    ["EMAIL", order?.email || "-"],
  ];
  custRow.forEach(([label, value], idx) => {
    const x = marginX + idx * infoColWidth;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY);
    doc.text(label, x, y);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...DARK);
    doc.text(String(value), x, y + 5.5, { maxWidth: infoColWidth - 4 });
  });

  y += 16;

  // ---- Pickup / drop address cards ----
  const boxGap = 6;
  const boxWidth = (contentWidth - boxGap) / 2;
  const pickup = formatAddress(order?.pickupAddress);
  const drop = formatAddress(order?.dropAddress);

  const renderAddressBox = (x, title, addr, bulletShape) => {
    let by = y;
    doc.setFillColor(...HEADER_DARK);
    doc.rect(x, by, boxWidth, 7.5, "F");
    doc.setFillColor(...PRIMARY);
    if (bulletShape === "circle") {
      doc.circle(x + 4.5, by + 3.75, 1.3, "F");
    } else {
      doc.rect(x + 3.3, by + 2.5, 2.4, 2.4, "F");
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(title, x + 8, by + 5);
    by += 7.5;

    const fullAddressText = addr.houseNo
      ? `${addr.line}, ${addr.houseNo}`
      : addr.line;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const addressLines = doc.splitTextToSize(fullAddressText, boxWidth - 6);

    const bodyHeight = addressLines.length * 4.6 + 8;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.rect(x, by, boxWidth, bodyHeight);

    doc.setTextColor(...DARK);
    doc.text(addressLines, x + 3, by + 6);

    return by + bodyHeight;
  };

  const pickupBottom = renderAddressBox(
    marginX,
    "PICKUP ADDRESS",
    pickup,
    "circle",
  );
  const dropBottom = renderAddressBox(
    marginX + boxWidth + boxGap,
    "DROP ADDRESS",
    drop,
    "circle",
  );
  y = Math.max(pickupBottom, dropBottom) + 10;

  // ---- Product details table ----
  sectionHeading("PRODUCT DETAILS");

  const totalQuantity = products.reduce(
    (sum, p) => sum + (Number(p?.quantity) || 0),
    0,
  );
  const totalWeight = products.reduce(
    (sum, p) => sum + (Number(p?.weight) || 0),
    0,
  );

  const tableBody = products.map((p, idx) => [
    idx + 1,
    "",
    p?.name || "-",
    p?.quantity ?? "-",
    p?.weight ?? "-",
    p?.height
      ? `${p.height}${p?.heightOption ? " " + p.heightOption : ""}`
      : "-",
    p?.width ? `${p.width}${p?.widthOption ? " " + p.widthOption : ""}` : "-",
  ]);

  const IMAGE_COL = 1;

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [
      [
        "#",
        "IMAGE",
        "ITEM NAME",
        "QUANTITY",
        "WEIGHT\n(KG)",
        "HEIGHT\n(CM)",
        "WIDTH\n(CM)",
      ],
    ],
    body: tableBody,
    foot: [["", "", "Total", totalQuantity, totalWeight, "", ""]],
    theme: "plain",
    styles: {
      fontSize: 9,
      textColor: DARK,
      lineColor: BORDER,
      lineWidth: { top: 0, left: 0, right: 0, bottom: 0.15 },
      cellPadding: 3,
      minCellHeight: 18,
      valign: "middle",
    },
    headStyles: {
      fillColor: LIGHT_GRAY,
      textColor: GRAY,
      fontStyle: "bold",
      fontSize: 7,
      minCellHeight: 9,
      lineWidth: { top: 0, left: 0, right: 0, bottom: 0.6 },
      lineColor: PRIMARY,
    },
    footStyles: {
      fillColor: LIGHT_GRAY,
      textColor: DARK,
      fontStyle: "bold",
      fontSize: 9.5,
      minCellHeight: 9,
      lineWidth: 0,
    },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 20, halign: "center" },
      3: { cellWidth: 22, halign: "center" },
      4: { cellWidth: 22, halign: "center" },
      5: { cellWidth: 22, halign: "center" },
      6: { cellWidth: 22, halign: "center" },
    },
    didParseCell: (data) => {
      if (
        data.section === "foot" &&
        (data.column.index === 3 || data.column.index === 4)
      ) {
        data.cell.styles.halign = "center";
      }
    },
    didDrawCell: (data) => {
      if (data.column.index !== IMAGE_COL) return;
      const size = 12;
      const cx = data.cell.x + data.cell.width / 2 - size / 2;
      const cy = data.cell.y + data.cell.height / 2 - size / 2;

      if (data.section === "body") {
        const dataUrl = productImages[data.row.index];
        if (dataUrl) {
          try {
            doc.addImage(dataUrl, cx, cy, size, size);
          } catch {
            drawImagePlaceholder(doc, cx, cy, size);
          }
        } else {
          drawImagePlaceholder(doc, cx, cy, size);
        }
      }
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // ---- Detailed requirements ----
  if (order?.description) {
    sectionHeading("DETAILED REQUIREMENTS");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const reqLines = doc.splitTextToSize(order.description, contentWidth - 6);
    const boxHeight = reqLines.length * 4.5 + 6;

    if (y + boxHeight > 265) {
      doc.addPage();
      y = 20;
    }

    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.roundedRect(marginX, y, contentWidth, boxHeight, 1.5, 1.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(reqLines, marginX + 3, y + 5.5);
    y += boxHeight + 14;
  } else {
    y += 6;
  }

  drawSignatureAndFooter(doc, {
    y,
    marginX,
    pageWidth,
    contentWidth,
    leftLabel: "CUSTOMER SIGNATURE & DATE",
    rightLabel: "QUIMERA REPRESENTATIVE & DATE",
  });

  doc.save(`WorkOrder_${order?.orderId || order?._id || "quimera"}.pdf`);
};

const STATUS_META = {
  completed: { label: "DELIVERED", bg: [230, 247, 235], color: GREEN },
  cancelled: { label: "CANCELLED", bg: [253, 232, 230], color: RED },
};

const getStatusMeta = (status) =>
  STATUS_META[status] || {
    label: status ? status.toUpperCase() : "IN PROGRESS",
    bg: [255, 241, 230],
    color: PRIMARY,
  };

export const generateDeliveryOrderPdf = async (order) => {
  if (!order) return;

  const products = order?.productDetails || [];
  const logoDataUrl = await loadLogoDataUrl();

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 15;
  const contentWidth = pageWidth - marginX * 2;
  const pageHeight = doc.internal.pageSize.getHeight();

  let y = drawBrandHeader(doc, {
    marginX,
    pageWidth,
    logoDataUrl,
    title: "DELIVERY ORDER",
    orderId: order?.orderId,
  });

  const orderHistory = order?.orderHistory || [];
  const deliveredStep =
    orderHistory.find(
      (step) =>
        (step?.status || step?.customStatus || "").toLowerCase() ===
        "delivered",
    ) ||
    [...orderHistory].reverse().find((step) => step?.completed) ||
    orderHistory[orderHistory.length - 1];

  // ---- Booking / pickup / delivery date / vehicle no. strip ----
  const infoColWidth = contentWidth / 4;
  const stripHeight = 16;
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.rect(marginX, y, contentWidth, stripHeight);
  doc.setLineWidth(0.9);
  doc.setDrawColor(...PRIMARY);
  doc.line(marginX, y, pageWidth - marginX, y);
  doc.setLineWidth(0.2);
  doc.setDrawColor(...BORDER);
  for (let i = 1; i <= 3; i++) {
    doc.line(
      marginX + infoColWidth * i,
      y + 2,
      marginX + infoColWidth * i,
      y + stripHeight - 2,
    );
  }

  const stripRow = [
    [
      "BOOKING DATE",
      order?.createdAt ? moment(order.createdAt).format("DD MMMM YYYY") : "-",
    ],
    [
      "PICKUP DATE",
      order?.pickupTime ? moment(order.pickupTime).format("DD MMMM YYYY") : "-",
    ],
    [
      "DELIVERY DATE",
      deliveredStep?.timestamp
        ? moment(deliveredStep.timestamp).format("DD MMMM YYYY")
        : "-",
    ],
    ["TRACK NUMBER", order?.vehicleNumber || order?.trackNumber || "-"],
  ];
  stripRow.forEach(([label, value], idx) => {
    const x = marginX + idx * infoColWidth + 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...GRAY);
    doc.text(label, x, y + 6);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(String(value), x, y + 12, { maxWidth: infoColWidth - 7 });
  });

  y += stripHeight + 8;

  // ---- Current status bar ----
  const statusMeta = getStatusMeta(order?.status);
  const statusBarHeight = 9;
  doc.setFillColor(...statusMeta.bg);
  doc.roundedRect(marginX, y, contentWidth, statusBarHeight, 1.2, 1.2, "F");
  doc.setFillColor(...statusMeta.color);
  doc.circle(marginX + 5, y + statusBarHeight / 2, 1.3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...statusMeta.color);
  doc.text(
    `CURRENT STATUS: ${statusMeta.label}`,
    marginX + 9,
    y + statusBarHeight / 2 + 1.3,
  );

  const statusTimestamp = deliveredStep?.timestamp
    ? moment(deliveredStep.timestamp).format("DD MMM YYYY · hh:mm A")
    : order?.updatedAt
      ? moment(order.updatedAt).format("DD MMM YYYY · hh:mm A")
      : "-";
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text(
    statusTimestamp,
    pageWidth - marginX - 3,
    y + statusBarHeight / 2 + 1.3,
    {
      align: "right",
    },
  );

  y += statusBarHeight + 10;

  const sectionHeading = (label) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...PRIMARY);
    doc.text(label, marginX, y);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.line(marginX, y + 2, pageWidth - marginX, y + 2);
    y += 8;
  };

  // ---- Customer & route cards ----
  const boxGap = 6;
  const boxWidth = (contentWidth - boxGap) / 2;
  const cardHeaderHeight = 12;

  const measureCard = (rows) => {
    let h = cardHeaderHeight;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    rows.forEach(([, value]) => {
      const lines = doc.splitTextToSize(String(value ?? "-"), boxWidth - 8);
      h += lines.length * 4.4 + 5.5;
    });
    return h + 2;
  };

  const renderInfoCard = (x, title, rows) => {
    const cardHeight = measureCard(rows);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.2);
    doc.rect(x, y, boxWidth, cardHeight);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...PRIMARY);
    doc.text(title, x + 4, y + 6);

    let by = y + cardHeaderHeight;
    rows.forEach(([label, value]) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      const lines = doc.splitTextToSize(String(value ?? "-"), boxWidth - 8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.8);
      doc.setTextColor(...GRAY);
      doc.text(label, x + 4, by);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.5);
      doc.setTextColor(...DARK);
      doc.text(lines, x + 4, by + 4.4);
      by += lines.length * 4.4 + 5.5;
    });

    return cardHeight;
  };

  const customerRows = [
    [
      "NAME",
      `${order?.name || "-"}${
        order?.accountType ? ` · ${capitalize(order.accountType)} user` : ""
      }`,
    ],
    ["PHONE", order?.phoneNumber || "-"],
    ["EMAIL", order?.email || "-"],
  ];
  const pickupAddr = formatAddress(order?.pickupAddress);
  const dropAddr = formatAddress(order?.dropAddress);
  const routeRows = [
    [
      "PICKUP",
      pickupAddr.houseNo
        ? `${pickupAddr.line}, ${pickupAddr.houseNo}`
        : pickupAddr.line,
    ],
    [
      "DROP",
      dropAddr.houseNo
        ? `${dropAddr.line}, ${dropAddr.houseNo}`
        : dropAddr.line,
    ],
  ];

  const customerHeight = renderInfoCard(marginX, "CUSTOMER", customerRows);
  const routeHeight = renderInfoCard(
    marginX + boxWidth + boxGap,
    "ROUTE",
    routeRows,
  );
  y += Math.max(customerHeight, routeHeight) + 10;

  // ---- Consignment table ----
  sectionHeading("CONSIGNMENT");

  const totalQuantity = products.reduce(
    (sum, p) => sum + (Number(p?.quantity) || 0),
    0,
  );
  const totalWeight = products.reduce(
    (sum, p) => sum + (Number(p?.weight) || 0),
    0,
  );

  const tableBody = products.map((p, idx) => [
    idx + 1,
    p?.name || "-",
    p?.quantity ?? "-",
    p?.weight ?? "-",
    p?.conditionOnDelivery || p?.condition || "-",
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: marginX, right: marginX },
    head: [
      ["#", "ITEM NAME", "QUANTITY", "WEIGHT (KG)", "CONDITION ON DELIVERY"],
    ],
    body: tableBody,
    foot: [["", "Total", totalQuantity, totalWeight, ""]],
    theme: "plain",
    styles: {
      fontSize: 9,
      textColor: DARK,
      lineColor: BORDER,
      lineWidth: { top: 0, left: 0, right: 0, bottom: 0.15 },
      cellPadding: 3,
      valign: "middle",
    },
    headStyles: {
      fillColor: LIGHT_GRAY,
      textColor: GRAY,
      fontStyle: "bold",
      fontSize: 7,
      minCellHeight: 9,
      lineWidth: { top: 0, left: 0, right: 0, bottom: 0.6 },
      lineColor: PRIMARY,
    },
    footStyles: {
      fillColor: LIGHT_GRAY,
      textColor: DARK,
      fontStyle: "bold",
      fontSize: 9.5,
      minCellHeight: 9,
      lineWidth: 0,
    },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      2: { cellWidth: 24, halign: "center" },
      3: { cellWidth: 28, halign: "center" },
      4: { cellWidth: 50 },
    },
    didParseCell: (data) => {
      if (
        data.section === "foot" &&
        (data.column.index === 2 || data.column.index === 3)
      ) {
        data.cell.styles.halign = "center";
      }
      if (data.section === "body" && data.column.index === 1) {
        data.cell.styles.textColor = PRIMARY;
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // ---- Order status history ----
  if (orderHistory.length) {
    sectionHeading("ORDER STATUS HISTORY");

    const dateColWidth = 26;
    const dotX = marginX + dateColWidth + 4;
    const textX = dotX + 6;
    const dateTimeGap = 20 * (25.4 / 96); // 20px converted to mm
    const dateTextX = dotX - dateTimeGap;

    orderHistory.forEach((step, idx) => {
      const isLast = idx === orderHistory.length - 1;
      const dotColor = step?.completed ? (isLast ? GREEN : PRIMARY) : BORDER;

      const title = capitalize(step?.customStatus || step?.status || "-");
      const message = step?.message || "";
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      const messageLines = message
        ? doc.splitTextToSize(message, contentWidth - dateColWidth - 10)
        : [];
      const rowHeight = Math.max(11, messageLines.length * 4 + 8);

      if (y + rowHeight > pageHeight - 45) {
        doc.addPage();
        y = 20;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...DARK);
      doc.text(
        step?.timestamp ? moment(step.timestamp).format("DD MMM YYYY") : "-",
        dateTextX,
        y + 3,
        { align: "right" },
      );
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      doc.text(
        step?.timestamp ? moment(step.timestamp).format("hh:mm A") : "",
        dateTextX,
        y + 7,
        { align: "right" },
      );

      doc.setFillColor(...dotColor);
      doc.circle(dotX, y + 2.5, 1.5, "F");
      if (!isLast) {
        doc.setDrawColor(...(step?.completed ? PRIMARY : BORDER));
        doc.setLineWidth(0.4);
        doc.line(dotX, y + 4, dotX, y + rowHeight);
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...DARK);
      doc.text(title, textX, y + 3);
      if (messageLines.length) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...GRAY);
        doc.text(messageLines, textX, y + 8);
      }

      y += rowHeight;
    });

    y += 6;
  } else {
    y += 4;
  }

  drawSignatureAndFooter(doc, {
    y,
    marginX,
    pageWidth,
    contentWidth,
    leftLabel: "RECEIVED BY (NAME & SIGNATURE)",
    rightLabel: "DELIVERY AGENT & DATE",
  });

  doc.save(`DeliveryOrder_${order?.orderId || order?._id || "quimera"}.pdf`);
};
